import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  AfterViewInit,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import {
  Question,
  QuestionnaireTemplate,
} from "../../models/questionnaire.model";
import { Router } from "@angular/router";
import { QuestionnaireService } from "../../services/questionnaire.service";
import { ResponseService } from "../../services/response.service";
import { ActivatedRoute, Params } from "@angular/router";
import { ContentService } from "../../services/content.service";
import { LocationStrategy } from "@angular/common";
import { NotificationRequest } from "../../models/notification.model";
import { Observable, catchError, forkJoin, map, of, switchMap } from "rxjs";
import { NotificationsService } from "../../services/notifications.service";
import { formatSubmissionDate } from "../../utils/utils";
import { JourneySectionHelperService } from "../../services/journey-section-helper.service";
import { ProfileInfo } from "../../models/donorProfile.model";
import { LocaleService } from "../../services/locale.service";

type StepState = "default" | "active" | "completed";

const comprehensiveQuestionnaireCopy = {
  en: {
    heading: "Comprehensive Eligibility Questionnaire",
    instructionsBefore: "Please answer all the questions and click",
    nextSectionLabel: "next section",
    instructionsAfter: "when you are ready.",
    loadingQuestions: "Loading questions...",
    adjustingQuestions: "Adjusting questions based on your responses...",
    savingResponses: "Saving responses...",
    savedBefore: "Your responses for",
    savedAfter: "have been saved successfully!",
    openingNextBefore: "Opening next section in",
    openingNextAfter: "seconds...",
    errorHeading: "Oh snap!",
    submitLabel: "Submit",
    nextButtonLabel: "Next Section",
  },
  es: {
    heading: "Cuestionario Completo de Elegibilidad",
    instructionsBefore: "Responda todas las preguntas y haga clic en",
    nextSectionLabel: "siguiente sección",
    instructionsAfter: "cuando esté listo.",
    loadingQuestions: "Cargando preguntas...",
    adjustingQuestions: "Ajustando las preguntas según sus respuestas...",
    savingResponses: "Guardando respuestas...",
    savedBefore: "Sus respuestas para",
    savedAfter: "se han guardado correctamente.",
    openingNextBefore: "Abriendo la siguiente sección en",
    openingNextAfter: "segundos...",
    errorHeading: "Algo salió mal.",
    submitLabel: "Enviar",
    nextButtonLabel: "Siguiente sección",
  },
};

@Component({
  selector: "app-comprehensive-questionnaire",
  templateUrl: "./comprehensive-questionnaire.component.html",
  styleUrl: "./comprehensive-questionnaire.component.scss",
})
export class ComprehensiveQuestionnaireComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  questionnaireForm!: NgForm;
  questionnaire: QuestionnaireTemplate | null = null;
  questionnaireId: string | null = null;
  templateName = "comprehensiveQuestionnaire";
  templateId = "QT_a5ae2d83-b520-49ae-86a7-db3959be3f41";
  isLoadingQuestionnaire = true;
  isLoadingResponses = true;
  isSavingResponses = false;
  hasCompleted = false;
  questionnareCompleted = false;
  errorMessage: string | null = null;
  redirectTime = 3;
  donorSectionId = "";
  donorProfileInfo: any;

  currentSectionIndex = 0;
  currentSectionName = "";
  firstIncompleteSection = 0;

  isContentScrollable = false;
  private readonly timingStoragePrefix = "questionnaireTiming";

  steps: { label: string; state: StepState }[] = [];

  get copy() {
    return (
      comprehensiveQuestionnaireCopy[
        this.localeService.currentLocale as keyof typeof comprehensiveQuestionnaireCopy
      ] || comprehensiveQuestionnaireCopy.en
    );
  }

  constructor(
    private questionnaireService: QuestionnaireService,
    private resService: ResponseService,
    private contentService: ContentService,
    private notificationsService: NotificationsService,
    private journeyHelper: JourneySectionHelperService,
    private router: Router,
    private route: ActivatedRoute,
    private locationStrategy: LocationStrategy,
    private localeService: LocaleService
  ) {}

  @HostListener("window:scroll", [])
  onWindowScroll() {
    this.updateScrollIndicator();
  }

  ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      const sectionIndex = parseInt(params["sectionIndex"], 10);
      this.currentSectionIndex = isNaN(sectionIndex) ? 0 : sectionIndex;
      this.updateProgressTracker();
      this.tryStartTiming();
    });

    this.detectBackButtonPress();
    this.handlePageLoad();
  }

  ngOnDestroy(): void {
    window.removeEventListener("popstate", this.onBackButton);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateScrollIndicator(), 5000);
  }

  private updateScrollIndicator() {
    const doc = document.documentElement;
    const scrollTop = window.pageYOffset || doc.scrollTop;
    const viewportHeight = window.innerHeight;
    const totalHeight = doc.scrollHeight;

    // 5px buffer so you don’t turn it off too early
    this.isContentScrollable = scrollTop + viewportHeight < totalHeight - 5;
  }

  scrollDown(): void {
    window.scrollBy({
      top: window.innerHeight,
      behavior: "smooth",
    });
  }

  handlePageLoad() {
    this.syncLocaleFromDonorProfile().pipe(
      switchMap(() =>
        forkJoin([
          this.loadQuestionnaire(),
          this.getQuestionnaireByUserId(),
        ])
      )
    ).subscribe({
      next: () => {
        this.steps = this.questionnaire!.sections.map((section, index) => ({
          label: section.description,
          state:
            index < this.currentSectionIndex
              ? "completed"
              : index === this.currentSectionIndex
                ? "active"
                : "default",
        }));
        this.updateProgressTracker();

        this.isLoadingQuestionnaire = false;
        this.tryStartTiming();
      },
      error: (err) => {
        console.error("Error loading questionnaire or responses:", err);
        this.isLoadingQuestionnaire = false;
      },
    });
  }

  private syncLocaleFromDonorProfile(): Observable<void> {
    return this.contentService.getDonorProfile().pipe(
      map((profileInfo: ProfileInfo) => {
        this.localeService.setLocale(
          profileInfo?.data?.donor?.locale ||
            profileInfo?.data?.donor?.preferredLocale
        );
        return void 0;
      }),
      catchError(() => of(void 0))
    );
  }

  private detectBackButtonPress(): void {
    window.addEventListener("popstate", this.onBackButton);
  }

  private onBackButton = (): void => {
    console.log("Back button pressed!");
    this.handlePageLoad();
    // Handle back navigation logic here (e.g., prevent navigation, show a confirmation)
  };

  onFormInstanceCaptured(form: NgForm) {
    this.questionnaireForm = form;
  }

  onCurrentStepChange(newStep: number) {
    this.currentSectionIndex = newStep;
    this.updateProgressTracker();
    this.router.navigate(
      ["/questionnaire/cquestionnaire/section", this.currentSectionIndex],
      { replaceUrl: true }
    );
  }

  updateProgressTracker() {
    this.steps.forEach((step, index) => {
      if (index < this.currentSectionIndex) {
        step.state = "completed";
      } else if (index === this.currentSectionIndex) {
        step.state = "active";
      } else {
        step.state = "default";
      }
    });
  }

  private tryStartTiming(): void {
    if (
      this.questionnareCompleted ||
      !this.questionnaireId ||
      !this.questionnaire?.template_id
    ) {
      return;
    }

    const sectionId = this.getCurrentSectionId();
    this.ensureTimingStarted("questionnaire", "questionnaire_started", null);
    this.ensureTimingStarted("section", "section_started", sectionId);
  }

  private ensureTimingStarted(
    scope: "questionnaire" | "section",
    eventType: string,
    sectionId: string | null
  ): void {
    const startedAtKey = this.getTimingStartedAtKey(scope, sectionId);
    const startedEventKey = this.getTimingStartedEventKey(scope, sectionId);
    let startedAt = localStorage.getItem(startedAtKey);

    if (!startedAt) {
      startedAt = new Date().toISOString();
      localStorage.setItem(startedAtKey, startedAt);
    }

    if (!localStorage.getItem(startedEventKey)) {
      this.recordTimingEvent(eventType, startedAt, sectionId);
      localStorage.setItem(startedEventKey, "true");
    }
  }

  private completeTiming(
    scope: "questionnaire" | "section",
    eventType: string,
    sectionId: string | null
  ): void {
    const startedAtKey = this.getTimingStartedAtKey(scope, sectionId);
    const startedEventKey = this.getTimingStartedEventKey(scope, sectionId);
    const startedAt = localStorage.getItem(startedAtKey);

    if (!startedAt) {
      return;
    }

    const endedAt = new Date().toISOString();
    this.recordTimingEvent(eventType, startedAt, sectionId, endedAt);
    localStorage.removeItem(startedAtKey);
    localStorage.removeItem(startedEventKey);
  }

  private recordTimingEvent(
    eventType: string,
    startedAt: string,
    sectionId: string | null,
    endedAt?: string
  ): void {
    if (!this.questionnaireId || !this.questionnaire?.template_id) {
      return;
    }

    const payload = {
      questionnaireId: this.questionnaireId,
      templateId: this.questionnaire.template_id,
      sectionId,
      subsectionId: null,
      eventType,
      startedAt,
      locale: this.localeService.currentLocale,
      ...(endedAt
        ? {
            endedAt,
            durationMs:
              new Date(endedAt).getTime() - new Date(startedAt).getTime(),
          }
        : {}),
    };

    this.questionnaireService
      .recordQuestionnaireTiming(payload)
      .pipe(
        catchError((error) => {
          console.error("Questionnaire timing failed:", error);
          return of(null);
        })
      )
      .subscribe();
  }

  private getTimingStartedAtKey(
    scope: "questionnaire" | "section",
    sectionId: string | null
  ): string {
    return [
      this.timingStoragePrefix,
      this.questionnaireId,
      this.questionnaire?.template_id,
      scope,
      sectionId || "all",
      "startedAt",
    ].join(":");
  }

  private getTimingStartedEventKey(
    scope: "questionnaire" | "section",
    sectionId: string | null
  ): string {
    return [
      this.timingStoragePrefix,
      this.questionnaireId,
      this.questionnaire?.template_id,
      scope,
      sectionId || "all",
      "startedEventSent",
    ].join(":");
  }

  private getCurrentSectionId(): string | null {
    return (
      this.questionnaire?.sections?.[this.currentSectionIndex]?.section_id ||
      null
    );
  }

  private loadQuestionnaire(): Observable<void> {
    this.errorMessage = null;

    return this.questionnaireService
      .getQuestionnaireByName(this.templateName)
      .pipe(
        switchMap((data) => {
          this.questionnaire = data.template;

          return of(void 0);
        }),
        catchError((err) => {
          console.error("Error loading questionnaire:", err);
          this.errorMessage =
            "An error occurred while loading the questionnaire.";
          return of(void 0);
        })
      );
  }

  private getQuestionnaireByUserId(): Observable<void> {
    this.hasCompleted = false;
    this.isLoadingResponses = true;

    return this.resService.getQuestionnaireByUserId().pipe(
      switchMap((data: any) => {
        this.isLoadingResponses = false;

        if (data && data.length > 0) {
          const questionnaire = data.find(
            (response: any) => response.templateId === this.templateId
          );

          if (questionnaire) {
            this.questionnaireId = questionnaire.questionnaireId;

            if (this.questionnaireId) {
              return this.getAndInitializeJourneySectionsAndProfile(
                questionnaire.status
              ).pipe(
                switchMap(() => {
                  this.questionnareCompleted =
                    questionnaire.status.toLowerCase() === "completed";

                  if (this.questionnareCompleted) {
                    this.startCountdown(true);
                    return of(void 0);
                  }

                  if (questionnaire.firstIncompleteSection != null) {
                    this.firstIncompleteSection =
                      questionnaire.firstIncompleteSection;

                    // If current route is not the correct one, redirect
                    const currentSectionIndex = this.currentSectionIndex;
                    if (currentSectionIndex !== this.firstIncompleteSection) {
                      return this.router
                        .navigate([
                          `/questionnaire/cquestionnaire/section`,
                          this.firstIncompleteSection,
                        ])
                        .then(() => void 0);
                    }
                  }

                  return of(void 0);
                })
              );
            }
          }
        }

        // No existing questionnaire, initialize
        return this.resService
          .initializeQuestionnaireResponse(this.templateId)
          .pipe(
            switchMap((data: any) => {
              this.questionnaireId = data.questionnaireId;
              return this.getAndInitializeJourneySectionsAndProfile(
                data.status
              );
            }),
            map(() => void 0)
          );
      }),
      catchError((err) => {
        console.error("Failed to get Questionnaire Responses for User", err);
        this.isLoadingResponses = false;
        return of(void 0);
      })
    );
  }

  startCountdown(questionnaireCompleted = false) {
    this.redirectTime = 3;
    const interval = setInterval(() => {
      this.redirectTime--;

      const timerElement = document.getElementById("redirect-timer");
      if (timerElement) {
        timerElement.textContent = this.redirectTime.toString();
      }

      if (this.redirectTime <= 0) {
        clearInterval(interval);

        // this.currentSectionIndex++;

        if (questionnaireCompleted) {
          this.router
            .navigate(["/questionnaire/postcomprehensive"])
            .then(() => {
              this.locationStrategy.replaceState(
                {},
                "",
                `/questionnaire/postcomprehensive`,
                ""
              );
            });
        } else {
          this.hasCompleted = false;

          this.handlePageLoad();
        }
        // TODO: Change this route!!!!
        // Replace '/desired-route' with your actual route
      }
    }, 1000);
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: "smooth" }); // Use smooth for a smooth scrolling effect
  }

  // TODO: Move this to a centralized place
  convertNgbDateToString(date: any): string | null {
    if (!date) return null;
    const mm = date.month.toString().padStart(2, "0");
    const dd = date.day.toString().padStart(2, "0");
    const yyyy = date.year.toString();
    return `${mm}/${dd}/${yyyy}`;
  }

  getAnswerValue(
    question: Question,
    submittedAnswers: Record<string, any>
  ): string | string[] | null {
    let answer: string | string[] | null;

    if (question.question_type === "multi") {
      answer = submittedAnswers[question.question_id] || [];
    } else if (question.question_type === "date") {
      answer = this.convertNgbDateToString(
        submittedAnswers[question.question_id]
      );
    } else {
      answer = submittedAnswers[question.question_id];
    }
    if (this.isCurrencyQuestion(question) && typeof answer === "string") {
      answer = this.normalizeCurrencyAnswer(answer);
    }
    return this.trimAnswer(answer);
  }

  private isCurrencyQuestion(question: Question): boolean {
    const text = String(question.questiion_text || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

    return (
      /current.*income/.test(text) ||
      /household.*income/.test(text) ||
      /disposable.*income/.test(text) ||
      /ingreso.*actual/.test(text) ||
      /ingresos.*actuales/.test(text) ||
      /ingreso.*hogar/.test(text) ||
      /ingresos.*hogar/.test(text) ||
      /ingreso.*disponible/.test(text) ||
      /ingresos.*disponibles/.test(text)
    );
  }

  private normalizeCurrencyAnswer(answer: string): string {
    return answer.replace(/\D/g, "");
  }

  private trimAnswer(answer: string | string[] | null): string | string[] | null {
    if (Array.isArray(answer)) {
      return answer.map((value) =>
        typeof value === "string" ? value.trim() : value
      );
    }
    if (typeof answer === "string") {
      return answer.trim();
    }
    return answer;
  }

  private getAndInitializeJourneySectionsAndProfile(
    status: string
  ): Observable<void> {
    return forkJoin([
      this.journeyHelper.ensureJourneySectionsExist().pipe(
        switchMap((sections) => {
          this.donorSectionId = sections[1]?.sectionId;

          const link$ =
            this.questionnaireId && !sections[1].questionnaireId
              ? this.journeyHelper.linkQuestionnaireToSection(
                  this.donorSectionId,
                  this.questionnaireId
                )
              : of(null);

          const update$ = this.journeyHelper.updateSectionStatus(
            this.donorSectionId,
            status
          );

          return forkJoin([link$, update$]);
        })
      ),
      this.journeyHelper.ensureDonorProfileExists(),
    ]).pipe(map(() => void 0)); // Return void observable
  }

  private linkQuestionnaireWithSection(data: any) {
    const sectionId = data[1].sectionId;
    this.donorSectionId = sectionId;
    const linkedQuestionnaireId = data[1].questionnaireId;

    if (sectionId && this.questionnaireId && !linkedQuestionnaireId) {
      this.contentService
        .updateQuestionnaireIdOnJourneySectionForUser(
          sectionId,
          this.questionnaireId
        )
        .subscribe({
          next: (data) => {
            console.log("updating questionnaire: ", data);
          },
        });
    }
  }

  onSubmit(event: {
    currentSectionIndex: number;
    submittedAnswers: Record<string, any>;
  }) {
    if (event.currentSectionIndex > this.firstIncompleteSection) {
      // REFRESH PAGE!
      console.log("refreshing page!!!!!!");
      window.location.reload();
    }

    if (this.questionnaireForm.valid) {
      this.isSavingResponses = true;
      this.scrollToTop();

      const currentSection =
        this.questionnaire?.sections[event.currentSectionIndex];
      const completedSectionId = currentSection?.section_id || null;

      const formData = currentSection?.subsections.flatMap((subsection) =>
        subsection.questions.map((question) => ({
          sectionId: currentSection.section_id,
          subsectionId: subsection.subsection_id,
          questionId: question.question_id,
          answer: this.getAnswerValue(question, event.submittedAnswers),
        }))
      );

      this.currentSectionName = currentSection?.description || "";

      if (formData && this.questionnaireId) {
        this.resService
          .storeResponses(this.questionnaireId, formData)
          .subscribe({
            next: (data) => {
              this.isSavingResponses = false;
              this.hasCompleted = true;
              this.completeTiming(
                "section",
                "section_completed",
                completedSectionId
              );

              // Questionnaire is completed
              if (data.statusUpdated.toLowerCase() === "completed") {
                this.completeTiming(
                  "questionnaire",
                  "questionnaire_completed",
                  null
                );
                this.contentService
                  .getDonorProfile()
                  .pipe(
                    switchMap((donorProfile: ProfileInfo) => {
                      this.donorProfileInfo = donorProfile || null;

                      const baseUrl = window.location.origin;
                      const donorName = this.donorProfileInfo
                        ? `${this.donorProfileInfo.data.donor.firstName} ${this.donorProfileInfo.data.donor.lastName}`
                        : "Unknown Donor";

                      const notificationRequest: NotificationRequest = {
                        triggerEvent: "comprehensive_questionnaire_completed",
                        channel: "email",
                        mailingListName: "coordinator-notifications",
                        priority: "medium",
                        data: {
                          donorName,
                          submissionDate: formatSubmissionDate(new Date()),
                          reviewLink: this.donorProfileInfo
                            ? `${baseUrl}/hospital/donor/${this.donorProfileInfo?.data.donor.userId}/journey`
                            : `${baseUrl}/hospital/dashboard/donor`,
                        },
                      };

                      const sendNotification$ = this.notificationsService
                        .sendNotification(notificationRequest)
                        .pipe(
                          catchError((error) => {
                            console.error(
                              "Notification failed, but continuing:",
                              error
                            );
                            return of(null); // Ensures flow continues even if notification fails
                          })
                        );

                      const updateJourney$ = this.contentService
                        .updateJourneySectionStatusForUser(
                          this.donorSectionId,
                          data.statusUpdated
                        )
                        .pipe(
                          catchError((error) => {
                            console.error(
                              "Journey update failed, but continuing:",
                              error
                            );
                            return of(null);
                          })
                        );

                      return forkJoin([sendNotification$, updateJourney$]);
                    })
                  )
                  .subscribe({
                    next: () => {
                      this.startCountdown(true);
                    },
                    error: (error) => {
                      console.error(
                        "Error fetching donor profile or executing parallel tasks:",
                        error
                      );
                      this.isSavingResponses = false;
                    },
                  });
              } else {
                this.startCountdown();
              }
            },
            error: (error) => {
              console.error("Error storing responses:", error);
            },
          });
      }
    } else {
      console.error("Form is invalid");
    }
  }
}
